import { useCallback, useMemo, useRef } from 'react';
import { useRecoilValue } from 'recoil';

import { PaymentCardMethodsState } from 'global-stores';
import { isCardExpired, resolveDefault } from 'views/settings/billing/deck/derive';
import { paymentCardTypes } from '../../../constant';
import { useChoose } from '../../hooks';

/**
 * PICK A PAYMENT METHOD.
 *
 * Rendered in two places — the billing upgrade wizard and the identity flow —
 * so it carries none of either page's chrome.
 *
 * WHAT THIS REPLACED, AND WHY EACH PIECE WENT
 *
 *  • Three 192px icon tiles in a fixed `height: 308px` band. Most of the
 *    section was empty, which is what made it read as unfinished.
 *  • A floating "Selected" pill at `bottom: -12px`, hanging over the tile's
 *    own border. It looked like a rendering fault rather than a state.
 *  • Labels in `--color-text-light-90` (#363a45) — a LIGHT-theme token, so on
 *    the dark surface they were barely legible.
 *  • `&--wire { pointer-events: none }`, unconditional. Wire was dead for
 *    everyone, despite the whole downstream flow existing (`handleWireAccount`
 *    -> `WireScreen` in use-payments.ts, plus wire-page/wire.tsx). A method the
 *    product supports was disabled in CSS, which no amount of config could
 *    undo. Disabled is now a per-tile PROP with a stated reason, never a class.
 *  • Clickable `<div>`s with no keyboard handling at all. These are a
 *    single-choice group, so they are a radiogroup with roving tabindex.
 *
 * The tiles also now say what is already on file. `/payment-methods` is
 * already in recoil, so "Visa ••4242 · exp 04/27" costs no extra request — and
 * a picker that knows what you have is a materially better picker than three
 * anonymous icons.
 */

interface ITile {
	key: string;
	id: string;
	label: string;
	icon: string;
	meta: string;
	metaKind: 'none' | 'expired' | 'reason' | '';
	isDefault: boolean;
	disabled: boolean;
}

export const PaymentChoose = () => {
	const { choose, handleChoose } = useChoose();
	const methods = useRecoilValue(PaymentCardMethodsState);
	const groupRef = useRef<HTMLDivElement | null>(null);

	/* `PaymentCardMethodsState` is `atom<null | any>`, so destructuring a bare
	   `{}` fallback types every field as never-present. Shaped here instead. */
	const {
		cards = [],
		banks = [],
		wire = null,
		default: fallback = null,
	} = (methods ?? {}) as {
		cards?: any[];
		banks?: any[];
		wire?: any;
		default?: {method?: string | null; detail?: unknown} | null;
	};

	/* Which method the account would actually be charged on — read from BOTH
	   signals the way the billing deck does, rather than trusting `default` on
	   the card alone. */
	const resolved = useMemo(
		() => resolveDefault(cards, banks, wire, fallback ?? undefined),
		[cards, banks, wire, fallback]
	);

	const tiles: ITile[] = useMemo(() => {
		const card = resolved.card ?? cards[0] ?? null;
		const expired = isCardExpired(card);
		const bank = banks[0] ?? null;

		const metaFor = (id: string): [string, ITile['metaKind']] => {
			if (id === 'CardView') {
				if (!card) return ['Not set up yet', 'none'];
				const exp =
					card.exp_month && card.exp_year
						? ` · exp ${String(card.exp_month).padStart(2, '0')}/${String(
								card.exp_year
						  ).slice(-2)}`
						: '';
				/* brand + last4 + expiry only — never the full number */
				const line = `${card.brand ?? 'Card'} ••${card.last4 ?? '????'}${exp}`;
				return [expired ? `${line} · expired` : line, expired ? 'expired' : ''];
			}
			if (id === 'AchView') {
				if (!banks.length) return ['Not set up yet', 'none'];
				const name = bank?.bankName ?? bank?.bank_name ?? 'Bank account';
				return [
					`${name} · ${banks.length} account${banks.length > 1 ? 's' : ''} linked`,
					'',
				];
			}
			return ['Contact support to pay by wire', 'reason'];
		};

		return paymentCardTypes.map(m => {
			const [meta, metaKind] = metaFor(m.id);
			return {
				key: m.key,
				id: m.id,
				label: m.label,
				icon: m.icon,
				meta,
				metaKind,
				isDefault:
					resolved.designated &&
					((m.id === 'CardView' && resolved.kind === 'card') ||
						/* `'ach'`, not `'bank'` — resolveDefault never returns 'bank', so the
					   ACH DEFAULT chip could never appear. */
					(m.id === 'AchView' && resolved.kind === 'ach') ||
						(m.id === 'Wire' && resolved.kind === 'wire')),
				/* WIRE IS OFF — as DATA, with a reason the user can read.
				   The old build disabled it with an unconditional
				   `pointer-events: none` in CSS, so it rendered as a faded tile
				   that swallowed clicks silently and looked like a bug. Here the
				   tile is `aria-disabled`, still focusable and announced, and it
				   says why. Flipping wire back on is one boolean, not a
				   stylesheet edit. */
				disabled: m.id === 'Wire',
			};
		});
	}, [cards, banks, resolved]);

	/* Roving tabindex: the group holds one tab stop, arrows move within it and
	   select as they go, which is the standard radiogroup behaviour. */
	const onKeyDown = useCallback(
		(e: React.KeyboardEvent<HTMLButtonElement>) => {
			const keys = ['ArrowRight', 'ArrowDown', 'ArrowLeft', 'ArrowUp'];
			if (!keys.includes(e.key)) return;
			e.preventDefault();
			const selectable = tiles.filter(t => !t.disabled);
			if (!selectable.length) return;
			const at = Math.max(
				0,
				selectable.findIndex(t => t.id === choose)
			);
			const step = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? 1 : -1;
			const next = selectable[(at + step + selectable.length) % selectable.length];
			if (!next) return;
			handleChoose(next.id)();
			const el = groupRef.current?.querySelector<HTMLButtonElement>(
				`[data-tile="${next.id}"]`
			);
			el?.focus();
		},
		[tiles, choose, handleChoose]
	);

	return (
		<div
			className="pm-pick"
			role="radiogroup"
			aria-label="Payment method"
			ref={groupRef}
		>
			{tiles.map(tile => {
				const selected = choose === tile.id;
				return (
					<button
						type="button"
						role="radio"
						key={tile.key}
						data-tile={tile.id}
						className="pm-tile"
						aria-checked={selected}
						/* aria-disabled rather than `disabled`: the tile stays focusable
						   and announceable, so a screen-reader user learns the option
						   exists and why it is unavailable. */
						aria-disabled={tile.disabled || undefined}
						aria-describedby={tile.disabled ? `${tile.id}-reason` : undefined}
						tabIndex={selected ? 0 : -1}
						onClick={tile.disabled ? undefined : handleChoose(tile.id)}
						onKeyDown={onKeyDown}
					>
						<span className="pm-tile__icon" aria-hidden="true">
							<i className={tile.icon} />
						</span>
						<span className="pm-tile__body">
							<span className="pm-tile__labelrow">
								<span className="pm-tile__label">{tile.label}</span>
								{tile.isDefault && (
									<span className="pm-tile__chip">Default</span>
								)}
							</span>
							<span
								id={tile.disabled ? `${tile.id}-reason` : undefined}
								className={`pm-tile__meta${
									tile.metaKind ? ` pm-tile__meta--${tile.metaKind}` : ''
								}`}
							>
								{tile.meta}
							</span>
						</span>
						<span className="pm-tile__radio" aria-hidden="true" />
					</button>
				);
			})}
		</div>
	);
};
